"use client";

import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import "./styles/controlled-react-select.css";

interface Option {
  value: string;
  label: string;
}

interface ControlledReactSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  placeholder?: string;
  options: Option[];
  disabled?: boolean;
  required?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  helperText?: string;
  className?: string;
}

const ControlledReactSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  placeholder,
  options,
  disabled = false,
  required = false,
  isMulti = false,
  isClearable = true,
  isSearchable = true,
  helperText,
  className = "",
}: ControlledReactSelectProps<TFieldValues, TName>) => {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: { required: required ? "This field is required" : false },
  });

  // Custom styles for react-select
  const customStyles: StylesConfig<Option, boolean> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "2.5rem",
      borderColor: error
        ? "var(--form-border-error)"
        : state.isFocused
        ? "var(--form-border-focus)"
        : "var(--form-border)",
      borderRadius: "var(--form-radius-lg)",
      boxShadow: state.isFocused
        ? error
          ? "var(--form-shadow-error)"
          : "var(--form-shadow-focus)"
        : "var(--form-shadow)",
      "&:hover": {
        borderColor: error
          ? "var(--form-border-error)"
          : "var(--form-border-hover)",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "var(--form-text-muted)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--form-text-primary)",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "var(--form-bg-secondary)",
      borderRadius: "var(--form-radius-sm)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "var(--form-text-primary)",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "var(--form-text-muted)",
      "&:hover": {
        backgroundColor: "var(--form-text-error)",
        color: "white",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "var(--form-border-focus)"
        : state.isFocused
        ? "#eff6ff"
        : "white",
      color: state.isSelected ? "white" : "var(--form-text-primary)",
      "&:hover": {
        backgroundColor: state.isSelected
          ? "var(--form-border-focus)"
          : "#eff6ff",
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "var(--form-radius-lg)",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    }),
  };

  // Handle value conversion for react-select
  const getValue = () => {
    if (isMulti) {
      return Array.isArray(value)
        ? value
            .map((val: string) =>
              options.find((option) => option.value === val)
            )
            .filter(Boolean)
        : [];
    }
    return value
      ? options.find((option) => option.value === value) || null
      : null;
  };

  // Handle change event
  const handleChange = (selectedOption: any) => {
    if (isMulti) {
      const values = selectedOption
        ? selectedOption.map((option: Option) => option.value)
        : [];
      onChange(values);
    } else {
      onChange(selectedOption ? selectedOption.value : null);
    }
  };

  return (
    <div
      className={`form-field controlled-react-select ${
        error ? "form-field--error controlled-react-select--error" : ""
      } ${className}`}
    >
      {label && (
        <span
          className={`form-field__label ${
            required ? "form-field__label--required" : ""
          }`}
        >
          {label}
        </span>
      )}
      <div className="controlled-react-select__container">
        <Select
          ref={ref}
          value={getValue()}
          onChange={handleChange}
          onBlur={onBlur}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          isMulti={isMulti}
          isClearable={isClearable}
          isSearchable={isSearchable}
          styles={customStyles}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>
      {error && <span className="form-field__error">{error.message}</span>}
      {helperText && !error && (
        <span className="form-field__helper">{helperText}</span>
      )}
    </div>
  );
};

export { ControlledReactSelect };
export type { ControlledReactSelectProps, Option };
